import { FlagDefinitionApi } from '@app/api';
import { ApiError, toApiError } from '@app/utils/ApiError';
import * as React from 'react';
import { RuleDictionary } from './ruleDictionary';

/**
 * The rule dictionary, read fresh every time something needs it.
 *
 * Deliberately not cached beyond the request in flight. The service builds this at startup, by
 * reflection over the classes it was compiled with, so a field or a helper added there appears the
 * moment its pod restarts - and the editor has to show it then, not after the browser tab is
 * reloaded. The only sharing is between consumers that ask at the same moment, so opening the page
 * costs one request whether the editor, the guide, or both are on screen.
 */
let inFlight: Promise<RuleDictionary> | null = null;

const loadDictionary = (): Promise<RuleDictionary> => {
  if (!inFlight) {
    inFlight = FlagDefinitionApi.getDictionary().finally(() => {
      inFlight = null;
    });
  }
  return inFlight;
};

export interface RuleDictionaryState {
  /** Empty until it has loaded, and empty if it could not be loaded. */
  dictionary: RuleDictionary;
  isLoading: boolean;
  /** Why the load failed, or null. Worth saying out loud: without it there is no autocomplete. */
  error: ApiError | null;
}

export const useRuleDictionary = (): RuleDictionaryState => {
  const [state, setState] = React.useState<RuleDictionaryState>({
    dictionary: {},
    isLoading: true,
    error: null
  });

  React.useEffect(() => {
    let active = true;

    loadDictionary()
      .then((dictionary) => {
        if (active) setState({ dictionary: dictionary ?? {}, isLoading: false, error: null });
      })
      .catch((error) => {
        if (active) setState({ dictionary: {}, isLoading: false, error: toApiError(error) });
      });

    return () => {
      active = false;
    };
  }, []);

  return state;
};
